import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useGetSourcesQuery } from '../../../api';
import { groupSourcesByMoveGroup } from '../utils';
import { useSourcePolling } from './useSourcePolling';

export const useSourcesData = ({ userOrg, combinedSearch }) => {
    const [pagination, setPagination] = useState({ current: 1, pageSize: 100, total: 0 });
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [polledSourceUpdates, setPolledSourceUpdates] = useState(new Map());
    const didInitExpandedRef = useRef(false);
    const previousGroupCountRef = useRef(0);

    const {
        data: sourcesData,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useGetSourcesQuery(
        { userOrg, size: pagination.pageSize, page: pagination.current - 1, combined_search: combinedSearch },
        { refetchOnMountOrArgChange: true, refetchOnReconnect: true, keepUnusedDataFor: 0 },
    );

    const handleSourceUpdate = useCallback((sourceId, updatedSourceData) => {
        setPolledSourceUpdates((prev) => {
            const newMap = new Map(prev);
            newMap.set(sourceId, updatedSourceData);
            return newMap;
        });
    }, []);

    const tableData = useMemo(() => {
        if (!sourcesData?.content) return [];
        const updatedSources = sourcesData.content.map((source) => {
            const polledUpdate = polledSourceUpdates.get(source.id);
            return polledUpdate ? { ...source, ...polledUpdate } : source;
        });
        const grouped = groupSourcesByMoveGroup({ ...sourcesData, content: updatedSources });
        return grouped;
    }, [sourcesData, polledSourceUpdates]);

    const { pollingCount } = useSourcePolling(tableData, handleSourceUpdate);

    // Initialize expanded groups only once per search query; do not reset on polling
    useEffect(() => {
        // Reset initialization flag when search changes
        didInitExpandedRef.current = false;
    }, [combinedSearch]);

    useEffect(() => {
        if (!didInitExpandedRef.current && tableData.length > 0) {
            const groupKeys = tableData.filter((item) => item.isGroup).map((item) => item.key);
            setExpandedRowKeys(groupKeys);
            didInitExpandedRef.current = true;
            previousGroupCountRef.current = groupKeys.length;
        } else if (didInitExpandedRef.current && tableData.length > 0) {
            // Check if new groups were added (e.g., after source refresh)
            const currentGroupKeys = tableData.filter((item) => item.isGroup).map((item) => item.key);
            const currentGroupCount = currentGroupKeys.length;

            // If group count increased, expand the new groups
            if (currentGroupCount > previousGroupCountRef.current) {
                setExpandedRowKeys((prev) => {
                    const newGroups = currentGroupKeys.filter((key) => !prev.includes(key));
                    return [...prev, ...newGroups];
                });
                previousGroupCountRef.current = currentGroupCount;
            }
        }
    }, [tableData, setExpandedRowKeys]);

    // Prune expanded keys that no longer exist after data updates
    useEffect(() => {
        if (tableData.length === 0) return;
        const validKeys = new Set(tableData.map((item) => item.key));
        setExpandedRowKeys((prev) => prev.filter((key) => validKeys.has(key)));
    }, [tableData]);

    useEffect(() => {
        if (sourcesData?.page) {
            setPagination((prev) => ({
                ...prev,
                total: sourcesData.page.totalElements,
                current: sourcesData.page.number + 1,
            }));
        }
    }, [sourcesData]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [combinedSearch]);

    useEffect(() => {
        if (isFetching) {
            setPolledSourceUpdates(new Map());
        }
    }, [isFetching]);

    return {
        tableData,
        expandedRowKeys,
        setExpandedRowKeys,
        pagination,
        setPagination,
        polledSourceUpdates,
        pollingCount,
        isLoading,
        isFetching,
        error,
        refetch,
    };
};
